/**
 * TabPicker UI Table Component
 * 
 * Table component for displaying structured data with consistent styling.
 * Provides responsive tables with hover states and optional features.
 * 
 * @module TabPicker.UI.Table
 */

import React from 'react';

/**
 * Table component props interface
 * 
 * @interface TableProps
 * @property {string} [className] - Additional CSS classes
 * @property {React.ReactNode} children - Table content
 * @property {boolean} [responsive] - Enable horizontal scroll on small screens
 * @property {boolean} [striped] - Enable striped rows
 * @property {boolean} [hover] - Enable row hover effect
 */
export interface TableProps {
  className?: string;
  children: React.ReactNode;
  responsive?: boolean;
  striped?: boolean;
  hover?: boolean;
}

/**
 * Table component for structured data display
 * 
 * Provides consistent table styling with optional features like
 * responsive scrolling, striped rows, and hover effects.
 * 
 * @param {TableProps} props - Component props
 * @returns {JSX.Element} Table element
 * 
 * @example
 * <Table responsive hover>
 *   <TableHeader>
 *     <TableRow>
 *       <TableHead>Name</TableHead>
 *       <TableHead>Value</TableHead>
 *     </TableRow>
 *   </TableHeader>
 *   <TableBody>
 *     <TableRow>
 *       <TableCell>Item 1</TableCell>
 *       <TableCell>$100</TableCell>
 *     </TableRow>
 *   </TableBody>
 * </Table>
 */
export function Table({ 
  className = '', 
  children, 
  responsive = true,
  striped = false,
  hover = true
}: TableProps) {
  const tableClasses = `min-w-full text-left text-sm ${className}`;
  
  const table = (
    <table className={tableClasses}>
      {children}
    </table>
  );
  
  if (responsive) {
    return (
      <div className="overflow-x-auto rounded-xl border border-gray-200 bg-white shadow-sm">
        {table}
      </div>
    );
  }
  
  return table;
}

/**
 * Table header component props
 */
export interface TableHeaderProps {
  className?: string;
  children: React.ReactNode;
}

/**
 * Table header component
 * 
 * @param {TableHeaderProps} props - Component props
 * @returns {JSX.Element} Table header element
 */
export function TableHeader({ className = '', children }: TableHeaderProps) {
  return (
    <thead className={`sticky top-0 bg-gray-100/80 backdrop-blur ${className}`}>
      {children}
    </thead>
  );
}

/**
 * Table body component props
 */
export interface TableBodyProps {
  className?: string;
  children: React.ReactNode;
}

/**
 * Table body component
 * 
 * @param {TableBodyProps} props - Component props
 * @returns {JSX.Element} Table body element
 */
export function TableBody({ className = '', children }: TableBodyProps) {
  return (
    <tbody className={className}>
      {children}
    </tbody>
  );
}

/**
 * Table row component props
 */
export interface TableRowProps {
  className?: string;
  children: React.ReactNode;
  onClick?: () => void;
  hover?: boolean;
}

/**
 * Table row component
 * 
 * @param {TableRowProps} props - Component props
 * @returns {JSX.Element} Table row element
 */
export function TableRow({ 
  className = '', 
  children, 
  onClick,
  hover = true 
}: TableRowProps) {
  const baseClasses = 'border-t border-gray-100';
  const interactiveClasses = onClick ? 'cursor-pointer' : '';
  const hoverClasses = hover ? 'hover:bg-gray-50/60' : '';
  
  return (
    <tr 
      className={`${baseClasses} ${interactiveClasses} ${hoverClasses} ${className}`}
      onClick={onClick}
    >
      {children}
    </tr>
  );
}

/**
 * Table head cell component props
 */
export interface TableHeadProps {
  className?: string;
  children: React.ReactNode;
  align?: 'left' | 'center' | 'right';
  sortable?: boolean;
  onSort?: () => void;
}

/**
 * Table head cell component
 * 
 * @param {TableHeadProps} props - Component props
 * @returns {JSX.Element} Table head cell element
 */
export function TableHead({ 
  className = '', 
  children, 
  align = 'left',
  sortable = false,
  onSort
}: TableHeadProps) {
  const alignClasses = {
    left: 'text-left',
    center: 'text-center',
    right: 'text-right'
  };
  
  const content = sortable ? (
    <button
      className="flex items-center gap-1 font-medium hover:text-gray-900"
      onClick={onSort}
    >
      {children}
      <span className="text-gray-400">↕</span>
    </button>
  ) : (
    <span className="font-medium">{children}</span>
  );
  
  return (
    <th className={`px-4 py-3 text-gray-600 ${alignClasses[align]} ${className}`}>
      {content}
    </th>
  );
}

/**
 * Table cell component props
 */
export interface TableCellProps {
  className?: string;
  children: React.ReactNode;
  align?: 'left' | 'center' | 'right';
  colSpan?: number;
}

/**
 * Table cell component
 * 
 * @param {TableCellProps} props - Component props
 * @returns {JSX.Element} Table cell element
 */
export function TableCell({ 
  className = '', 
  children, 
  align = 'left',
  colSpan
}: TableCellProps) {
  const alignClasses = {
    left: 'text-left',
    center: 'text-center',
    right: 'text-right'
  };
  
  return (
    <td 
      className={`px-4 py-2 ${alignClasses[align]} ${className}`}
      colSpan={colSpan}
    >
      {children}
    </td>
  );
}

/**
 * Table empty state component props
 */
export interface TableEmptyProps {
  message?: string;
  action?: React.ReactNode;
}

/**
 * Table empty state component
 * 
 * @param {TableEmptyProps} props - Component props
 * @returns {JSX.Element} Table empty state element
 * 
 * @example
 * <TableEmpty 
 *   message="No data available"
 *   action={<Button size="sm">Add Item</Button>}
 * />
 */
export function TableEmpty({ 
  message = 'No data available', 
  action 
}: TableEmptyProps) {
  return (
    <TableRow hover={false}>
      <TableCell colSpan={100} align="center">
        <div className="py-8 text-gray-500">
          <p>{message}</p>
          {action && <div className="mt-4">{action}</div>}
        </div>
      </TableCell>
    </TableRow>
  );
}